import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInMonths, isSameDay } from 'date-fns';

import { OmaLummeApp } from '../../../omaLummeContext';
import { ManageContractsApp } from './contractManagementContext';

// Types.
import { Delivery, OmaLummeAddOnTemplate, OmaLummeTariffElement } from '../../../types/contract';
import { ManageContractsSummaryProps } from '../../../types/contract';
// Utils.
import { generateAddress } from '../../../utils/address';
import { formatDate, setPriceFormat } from '../../../utils/billing';
import { PriceFixTariffNo } from '../../../utils/contracts';
import { SummaryActionTitle } from '../../../utils/contractManagement';

import { useWindowSize } from '../../Layout/Header';

export const ManageContractsSummary: React.FunctionComponent<ManageContractsSummaryProps> = (props: ManageContractsSummaryProps) => {
  const { t } = useTranslation();
  const { campaigns, marketingPermissions, marketingEmail, marketingSMS } = props;

  const { users } = OmaLummeApp.useState();
  const {
    selectedForm,
    selectedForm2,
    selectedForm4,
    oldContract,
    oldContractOwner,
    endDate,
    startDate,
    createNewContract,
    newAddress,
    primaryAddress,
    terminateLedgerAddress,
    terminateLedgerUseCurrentAddress,
    changeAddressDate,
    campaignOmaLummePrices,
    newContract,
    extraServices,
    payment,
    multipleOwners,
    multipleOwnersNewAddress,
  } = ManageContractsApp.useState();

  const windowWidth = useWindowSize()[0];

  let externalName: string | undefined = undefined;
  let basePrice: string | undefined = undefined;
  let energyDeliveries: Delivery[] = [];
  let energyPrice: string | undefined = undefined;
  let marginPrice: string | undefined = undefined;
  let duration: number | undefined = undefined;
  let baseOffer: OmaLummeTariffElement | undefined = undefined;
  let contractServices: OmaLummeAddOnTemplate[] | undefined = undefined;

  let useOldContract = false;

  const selectedNewContract = newContract;

  if (selectedNewContract) {
    useOldContract = selectedNewContract === '0-0';
    const newContract = campaignOmaLummePrices.find((c) => c.contractTemplateNo.toString() === selectedNewContract.split('-')[0]);
    const newTariff = newContract?.tariffs.find((t) => t.tariffNo.toString() === selectedNewContract.split('-')[1]);

    if (!useOldContract) {
      externalName = newTariff?.externalName;
      basePrice = newTariff?.tariffElements
        .find((element) => element.elementNo === 1)
        ?.priceInclVat.toFixed(2)
        .replace('.', ',');
      energyPrice =
        newTariff?.externalName.toLowerCase().includes('pörssi') || newTariff?.externalName.toLowerCase().includes(' spot')
          ? 'Spot'
          : `${newTariff?.tariffElements
              .find((element) => element.elementNo === 2)
              ?.priceInclVat.toFixed(2)
              .replace('.', ',')} ${t('manageContracts:manageContracts.TableEnergyUnit')}`;
      marginPrice =
        newTariff?.externalName.toLowerCase().includes('pörssi') || newTariff?.externalName.toLowerCase().includes(' spot')
          ? newTariff?.tariffElements
              .find((element) => element.elementNo === 3)
              ?.priceInclVat.toFixed(2)
              .replace('.', ',')
          : undefined;
      duration = newTariff?.duration;
      baseOffer = newTariff?.tariffElements.find((element) => element.elementNo === 21);
      contractServices = newContract?.addOnTemplates.filter((t) => extraServices.includes(t.addonProductTemplateNo));
    }
  }

  if ((useOldContract || (selectedForm === 4 && selectedForm4 === 3)) && oldContract && oldContract.activeDeliveries) {
    const baseDelivery = oldContract.activeDeliveries[0];

    const energyPriceAmount = oldContract.activeDeliveries.find((element) => element.elementNo === 2)?.priceInclVat;

    const spotPriceFixes = oldContract?.deliveries
      ?.sort((a, b) => (new Date(a.specialToDate || new Date()) < new Date(b.specialToDate || new Date()) ? -1 : 1))
      .filter(
        (d) =>
          d.tariffNo === PriceFixTariffNo &&
          d.elementNo === 10 &&
          new Date(d.specialToDate || new Date()) > new Date() &&
          !isSameDay(new Date(d.specialToDate || new Date()), new Date('2073-06-01')),
      );

    energyDeliveries = spotPriceFixes ?? oldContract.activeDeliveries.filter((d) => d.amountUnit === 'kWh');

    externalName = baseDelivery.externalName;
    basePrice = oldContract.activeDeliveries
      .find((element) => element.elementNo === 1 || element.elementNo === 3 || element.elementNo === 6)
      ?.priceInclVat.toFixed(2)
      .replace('.', ','); // 1 = kuukausimaksu 3 = kuukausimaksu päivä/yö 6 = kuukausimaksu vuodenaika
    energyPrice =
      baseDelivery.externalName.toLowerCase().includes('pörssi') || baseDelivery.externalName.toLowerCase().includes(' spot')
        ? 'Spot'
        : energyPriceAmount
          ? `${energyPriceAmount.toFixed(2).replace('.', ',')} ${t('manageContracts:manageContracts.TableEnergyUnit')}`
          : undefined;
    marginPrice =
      baseDelivery.externalName.toLowerCase().includes('pörssi') || baseDelivery.externalName.toLowerCase().includes(' spot')
        ? oldContract.activeDeliveries
            .find((element) => element.elementNo === 3)
            ?.priceInclVat.toFixed(2)
            .replace('.', ',')
        : undefined;
    if (oldContract.endDate === '2073-06-01T00:00:00' || baseDelivery.tariffToDate === '2073-06-01T00:00:00') {
      duration = 0;
    } else {
      duration = differenceInMonths(new Date(baseDelivery.tariffToDate), new Date(baseDelivery.tariffFromDate));
    }
    contractServices = campaigns
      .find((c) => c.addOnTemplates.filter((t) => extraServices.includes(t.addonProductTemplateNo)).length > 0)
      ?.addOnTemplates.filter((t) => extraServices.includes(t.addonProductTemplateNo));
  }

  let durationString = '';

  if (duration === 0) {
    durationString = t('contracts:contracts.ongoing');
  }
  if (duration && duration > 0 && useOldContract && oldContract && oldContract.activeDeliveries) {
    durationString = `${formatDate(oldContract.activeDeliveries[0].tariffToDate)} ${t('contracts:contracts.until')}`;
  }
  if (duration && duration > 0 && !useOldContract) {
    durationString = `${duration} ${t('manageContracts:manageContracts.TablePeriodUnit')}`;
  }

  if (selectedForm === 4 && selectedForm4 === 3) {
    durationString = t('contracts:contracts.ongoing');
  }

  const personDetails = oldContractOwner && oldContractOwner.userId !== users.activeUser.userId ? oldContractOwner : users.activeUser;

  return (
    <div className="manage-contracts-summary">
      {personDetails && (
        <section>
          <h2>{t('manageContracts:manageContracts.SummaryPersonTitle')}</h2>
          <div className="summaryFields">
            <div>
              <h4>{t('common:general.name')}</h4>
              <p>
                {personDetails.userFirstName} {personDetails.userLastName}
              </p>
            </div>
            <div>
              <h4>{t('common:general.customerNumber')}</h4>
              <p>{personDetails.userId}</p>
            </div>
            <div className="break" />
            <div>
              <h4>{t('common:general.phone')}</h4>
              <p>{personDetails.userNumber}</p>
            </div>
            <div>
              <h4>{t('common:general.email')}</h4>
              <p>{personDetails.userEmail}</p>
            </div>
            {personDetails.userAddress[0] && (
              <>
                <div className="break" />
                <div>
                  <h4>{t('common:general.address')}</h4>
                  <p>{generateAddress(personDetails.userAddress[0], true)}</p>
                </div>
                <div>
                  <h4>{t('common:general.zipcode')}</h4>
                  <p>{personDetails.userAddress[0].zipCode}</p>
                </div>
                <div>
                  <h4>{t('common:general.postalArea')}</h4>
                  <p>{personDetails.userAddress[0].cityName}</p>
                </div>
                <div>
                  <h4>{t('common:general.country')}</h4>
                  <p>{personDetails.userAddress[0].countryName}</p>
                </div>
              </>
            )}
          </div>
          {primaryAddress && changeAddressDate && (
            <>
              <h3>{t('manageContracts:manageContracts.SummaryPrimaryAddressTitle')}</h3>
              <div className="summaryFields">
                <div>
                  <h4>{t('common:general.address')}</h4>
                  <p>{generateAddress(primaryAddress, true)}</p>
                </div>
                <div>
                  <h4>{t('common:general.zipcode')}</h4>
                  <p>{primaryAddress.zipCode}</p>
                </div>
                <div>
                  <h4>{t('common:general.postalArea')}</h4>
                  <p>{primaryAddress.cityName}</p>
                </div>
                <div className="break" />
                <div>
                  <h4>{t('manageContracts:manageContracts.SummaryPrimaryAddressDate')}</h4>
                  <p>{formatDate(changeAddressDate.toString())}</p>
                </div>
              </div>
            </>
          )}
        </section>
      )}
      <section>
        <h2>{t('manageContracts:manageContracts.SummaryCurrentContractTitle')}</h2>
        <p>
          {t(SummaryActionTitle(selectedForm, selectedForm4))}
          {selectedForm === 2 && selectedForm2 !== 0 && ` ${t(`manageContracts:manageContracts.StartOption2Sub${selectedForm2}Title`).toLowerCase()}`}
        </p>
        {oldContract && endDate && (
          <div className="summaryFields">
            {(selectedForm4 === 0 || selectedForm4 === 1) && <h3>{t('manageContracts:manageContracts.SummaryEndingContractTitle')}</h3>}
            {(selectedForm4 === 2 || selectedForm4 === 3) && <h3>{t('manageContracts:manageContracts.SummaryChangingContractTitle')}</h3>}
            <div>
              <h4>{t('common:general.address')}</h4>
              <p>{oldContract && oldContract.meteringPoint && generateAddress(oldContract.meteringPoint, false)}</p>
            </div>
            <div>
              <h4>{t('common:general.contractNumber')}</h4>
              <p>{oldContract && oldContract.contractCode}</p>
            </div>
            {!(selectedForm === 4 && selectedForm4 === 3) && selectedForm4 !== 2 && (
              <>
                <div>
                  {selectedForm === 4 && selectedForm4 === 3 && <h4>{t('manageContracts:manageContracts.OldDateTitleForm4_3')}</h4>}
                  {!(selectedForm === 4 && selectedForm4 === 3) && <h4>{t('manageContracts:manageContracts.OldDateTitleForm1')}</h4>}
                  <p>{endDate && formatDate(endDate.toString())}</p>
                </div>
              </>
            )}
          </div>
        )}
      </section>
      {selectedForm !== 3 &&
        ((selectedForm === 1 && createNewContract) || (selectedForm !== 1 && selectedNewContract) || (selectedForm === 4 && selectedForm4 === 3)) && (
          <section>
            <div className="summaryFields">
              <h3>
                {!(selectedForm === 4 && selectedForm4 === 3)
                  ? t('manageContracts:manageContracts.SummaryNewContractTitle')
                  : t('manageContracts:manageContracts.SummaryAddExtraServicesTitle')}
              </h3>
              {!(selectedForm === 4 && selectedForm4 === 3) && (
                <>
                  <div>
                    <h4>{t('common:general.address')}</h4>
                    {newAddress && <p>{generateAddress(newAddress, false)}</p>}
                    {selectedForm === 4 && oldContract && oldContract.meteringPoint && <p>{generateAddress(oldContract.meteringPoint, false)}</p>}
                  </div>
                  <div className="break" />
                  <div>
                    <h4>{t('manageContracts:manageContracts.TableContract')}</h4>
                    <p>{externalName}</p>
                  </div>
                </>
              )}
              <div>
                <h4>
                  {!(selectedForm === 4 && selectedForm4 === 3)
                    ? t('manageContracts:manageContracts.NewDateTitleShort')
                    : t('manageContracts:manageContracts.ExtraServiceStartDateTitle')}
                </h4>
                <p>{startDate && formatDate(startDate.toString())}</p>
              </div>
              <div className="break" />
              {!(selectedForm === 4 && selectedForm4 === 3) && (
                <>
                  <div>
                    <h4>{t('manageContracts:manageContracts.TableBase')}</h4>
                    <p>
                      {basePrice} {t('manageContracts:manageContracts.TableBaseUnit')}
                    </p>
                    {baseOffer && baseOffer.discountInfo && baseOffer.discountInfo.duration > 0 && windowWidth < 992 && (
                      <p>
                        {t('manageContracts:manageContracts.TableCampaignOfferBody', {
                          duration: baseOffer.discountInfo?.duration ?? 0,
                          price: setPriceFormat((baseOffer.priceInclVat ?? 0).toString()),
                        })}
                      </p>
                    )}
                  </div>
                  {!energyPrice && energyDeliveries && (
                    <>
                      <div>
                        <h4>{t('manageContracts:manageContracts.TableEnergy')}</h4>
                        <p>
                          {energyDeliveries.map((d) => (
                            <>
                              {d.priceInclVat?.toFixed(2).replace('.', ',')}{' '}
                              {d.elementName.toLowerCase().replace('energia', '').replace('talviarki', 'talvi').replace('muuaika', 'muu')}{' '}
                            </>
                          ))}
                        </p>
                      </div>
                    </>
                  )}
                  {energyPrice && (
                    <>
                      <div>
                        <h4>{t('manageContracts:manageContracts.TableEnergy')}</h4>
                        <p>{energyPrice}</p>
                      </div>
                    </>
                  )}
                  {marginPrice && (
                    <>
                      <div>
                        <h4>{t('manageContracts:manageContracts.TableMargin')}</h4>
                        <p>
                          {marginPrice} {t('manageContracts:manageContracts.TableMarginUnit')}
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}
              <div>
                <h4>{t('manageContracts:manageContracts.TablePeriod')}</h4>
                <p>{durationString}</p>
              </div>

              {energyDeliveries && energyDeliveries?.length > 1 && energyDeliveries.find((d) => d.elementNo === 10) && (
                <div>
                  <h4>{t(`manageContracts:manageContracts.TableSpotPriceFixings`)}</h4>
                  <p>
                    {energyDeliveries.length} {t(`manageContracts:manageContracts.TableSpotPriceFixingsUnit`)}
                  </p>
                </div>
              )}
              {baseOffer && baseOffer.discountInfo && baseOffer.discountInfo.duration > 0 && windowWidth >= 992 && (
                <p className="notopmargin">
                  {t('manageContracts:manageContracts.TableCampaignOfferBody', {
                    duration: baseOffer.discountInfo?.duration ?? 0,
                    price: setPriceFormat((baseOffer.priceInclVat ?? 0).toString()),
                  })}
                </p>
              )}

              {contractServices && contractServices.length > 0 && (
                <>
                  <div className="break" />
                  <div>
                    <h4>{t('manageContracts:manageContracts.ExtraServicesTitle')}</h4>
                    {contractServices &&
                      contractServices.length > 0 &&
                      contractServices.map((service) => {
                        const elem = service.tariffs[0].tariffElements[0];

                        return (
                          <Fragment key={`${elem.elementName}-${elem.elementNo}`}>
                            <p>
                              {service.tariffs[0].externalName} {elem.priceInclVat.toFixed(2).replace('.', ',')}{' '}
                              {elem.unitPrice === 'EUR/kk' ? t('manageContracts:manageContracts.TableBaseUnit') : elem.unitPrice}
                            </p>
                          </Fragment>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          </section>
        )}

      <section>
        {!(selectedForm === 4 && selectedForm4 === 3) && (
          <>
            <h2>{t('manageContracts:manageContracts.SummaryInvoicingTitle')}</h2>
          </>
        )}
        {(selectedForm === 1 || selectedForm === 3) && !createNewContract && terminateLedgerAddress && (
          <div>
            <h4>{t('billing:paymentTypes.paper')}</h4>
            <p>{generateAddress(terminateLedgerUseCurrentAddress ? personDetails.userAddress[0] : terminateLedgerAddress, false)}</p>
          </div>
        )}
        {payment.paymentType === 'eBill' && payment.paymentTypeEbillBank && (
          <>
            <h4>{t('billing:paymentTypes.eBill')}</h4>
            <p>{payment.paymentTypeEbillBank}</p>
          </>
        )}
        {payment.paymentType === 'emailMyAddress' && (
          <>
            <h4>{t('billing:paymentTypes.email')}</h4>
            <p>{personDetails.userEmail}</p>
          </>
        )}
        {payment.paymentType === 'emailNewAddress' && payment.paymentTypeNewEmail && (
          <>
            <h4>{t('billing:paymentTypes.email')}</h4>
            <p>{payment.paymentTypeNewEmail}</p>
          </>
        )}
        {payment.paymentType === 'paperMyAddress' && (
          <>
            <h4>{t('billing:paymentTypes.paper')}</h4>
            <p>
              {primaryAddress && changeAddressDate
                ? generateAddress(primaryAddress, false)
                : generateAddress(personDetails.userAddress[0], false)}{' '}
            </p>
          </>
        )}
        {payment.paymentType === 'paperNewAddress' && payment.paymentTypeNewAddress && (
          <>
            <h4>{t('billing:paymentTypes.paper')}</h4>
            <p>
              {payment.paymentTypeNewAddress.firstname} {payment.paymentTypeNewAddress.lastname}
            </p>
            <p>{generateAddress(payment.paymentTypeNewAddress, false)}</p>
          </>
        )}
      </section>
      {((selectedForm === 1 && createNewContract) || selectedForm === 2) && multipleOwners && multipleOwnersNewAddress && (
        <section>
          <h2>{t('manageContracts:manageContracts.SummaryMultipleOwnersTitle')}</h2>
          <div className="summaryFields">
            <div>
              <h4>{t('common:general.name')}</h4>
              <p>
                {multipleOwnersNewAddress.firstname} {multipleOwnersNewAddress.lastname}
              </p>
            </div>
            <div>
              <h4>{t('common:general.SSN')}</h4>
              <p>{multipleOwnersNewAddress.ssn}</p>
            </div>
            <div className="break" />
            <div>
              <h4>{t('common:general.address')}</h4>
              <p>{generateAddress(multipleOwnersNewAddress, true)}</p>
            </div>
            <div>
              <h4>{t('common:general.zipcode')}</h4>
              <p>{multipleOwnersNewAddress.zipCode}</p>
            </div>
            <div>
              <h4>{t('common:general.postalArea')}</h4>
              <p>{multipleOwnersNewAddress.cityName}</p>
            </div>
            <div>
              <h4>{t('common:general.phone')}</h4>
              <p>{multipleOwnersNewAddress.mobileNumber}</p>
            </div>
          </div>
        </section>
      )}
      {!marketingPermissions && (marketingEmail || marketingSMS) && (
        <section>
          <h2>{t('manageContracts:manageContracts.SummaryMarketingTitle')}</h2>

          <p>{t(`manageContracts:manageContracts.ConfirmMarketingBody`)}</p>

          {marketingEmail && (
            <p>
              {t('manageContracts:manageContracts.ConfirmMarketingEmail')} <strong>{personDetails.userEmail}</strong>
            </p>
          )}
          {marketingSMS && (
            <p>
              {t('manageContracts:manageContracts.ConfirmMarketingSMS')} <strong>{personDetails.userNumber}</strong>
            </p>
          )}
        </section>
      )}
      <section>
        <p>{t(`manageContracts:manageContracts.PaperlessDisclaimer`)}</p>
      </section>
      <section>
        <p>
          {t(`manageContracts:manageContracts.OomiDisclaimer${new Date().getFullYear() > 2025 ? '2026' : '2025'}`)}
          {new Date().getFullYear() > 2025 ? (
            <>
              {' '}
              <a href="https://www.lumme-energia.fi/oomi" target="_blank" rel="noopener noreferrer">
                {t('manageContracts:manageContracts.OomiDisclaimer2026Link')}
              </a>
            </>
          ) : undefined}
        </p>
      </section>
    </div>
  );
};

export default ManageContractsSummary;
